import { PropsWithChildren, ReactNode } from 'react';
import { MdOutlineSync } from 'react-icons/md';
import { Button, ModalBody, ModalContent, ModalHeader, Spinner, useDisclosure } from '@ds';
import { DrawerModal } from '@ui';
import { WorkdaySyncHookData } from '@lp/features/Scheduler/types';
import {
  BaseResource,
  ResolveHrSystemConflict,
  ResourceShiftTypeHrSystemConflict,
  ShiftType,
} from '@/api/apis/resources/schedule';

type ConflictCardProps<R extends BaseResource, T extends ShiftType, P extends Record<string, unknown>> = {
  conflict: ResourceShiftTypeHrSystemConflict<R, T>;
  onConflictResolve: (resolveData: ResolveHrSystemConflict<P>) => void;
};

type HrSystemConflictsDrawerModalProps<
  R extends BaseResource,
  T extends ShiftType,
  P extends Record<string, unknown>,
> = PropsWithChildren<{
  hrSystemData?: WorkdaySyncHookData<R, T, P>;
  ConflictCard: (props: ConflictCardProps<R, T, P>) => ReactNode;
}>;

export const HrSystemConflictsDrawerModal = <
  R extends BaseResource,
  T extends ShiftType,
  P extends Record<string, unknown>,
>({
  hrSystemData,
  ConflictCard,
}: HrSystemConflictsDrawerModalProps<R, T, P>) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  if (!hrSystemData?.workdayConflicts?.length) return null;

  const { workdayConflicts, isConflictsLoading, onWorkdayConflictResolve } = hrSystemData;

  return (
    <>
      <Button
        variant="light"
        size="md"
        disableRipple
        isDisabled={isConflictsLoading}
        startContent={isConflictsLoading ? <Spinner size="sm" color="current" /> : <MdOutlineSync size={24} />}
        className="bg-base-50 min-w-[unset] mr-1"
        onPress={onOpen}
      >
        {workdayConflicts.length}
      </Button>
      <DrawerModal
        isOpen={isOpen}
        onClose={onClose}
        size="sm"
        className="bg-base-80"
        classNames={{
          closeButton: 'text-base-20 m-4 text-hl top-[-10px]',
        }}
      >
        <ModalContent>
          <ModalHeader className="pb-1">
            <h3 className="text-hl text-base-20">Workday Updates</h3>
          </ModalHeader>
          <ModalBody>
            {isConflictsLoading && (
              <div className="absolute flex items-center justify-center size-full left-0 top-12 backdrop-blur z-12">
                <Spinner size="lg" />
              </div>
            )}
            {workdayConflicts.map((conflict) => (
              <ConflictCard
                key={conflict.conflictId}
                conflict={conflict}
                onConflictResolve={onWorkdayConflictResolve}
              />
            ))}
          </ModalBody>
        </ModalContent>
      </DrawerModal>
    </>
  );
};
